/**
 * Complete Profile Page Component
 * Requires users missing email or company to add them before continuing
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Message, Card } from '../components/ui';
import './CompleteProfile.css';

const CompleteProfile = ({ onProfileComplete }) => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: '',
    organization: '',
  });
  const [message, setMessage] = useState({ text: '', type: '' });
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    if (type === 'error') {
      setTimeout(() => setMessage({ text: '', type: '' }), 5000);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.email.trim()) {
      showMessage('Email is required', 'error');
      return;
    }
    if (!formData.organization || !formData.organization.trim()) {
      showMessage('Company or organisation is required', 'error');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      showMessage('Please enter a valid email address', 'error');
      return;
    }

    setIsLoading(true);

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/auth/complete-profile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          email: formData.email.trim(),
          organization: formData.organization.trim(),
        }),
      });

      const data = await response.json();

      if (data.success) {
        showMessage('Profile updated. You can now continue.', 'success');

        if (onProfileComplete) {
          onProfileComplete();
        }

        setTimeout(() => {
          navigate('/');
        }, 1000);
      } else {
        showMessage(data.error || 'Failed to update profile', 'error');
      }
    } catch (error) {
      showMessage('Network error: ' + error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="rf-complete-profile-page">
      <div className="rf-center-container">
        <Card className="rf-complete-profile-card">
          <div className="rf-auth-logo">
            <i className="fas fa-user-edit"></i>
            <span>Complete Your Profile</span>
          </div>

          <div className="rf-profile-notice">
            <i className="fas fa-info-circle"></i>
            <p>
              Your account is missing an email address or company. Please add them below to continue.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="rf-complete-profile-form">
            <Input
              label="Email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              autoComplete="email"
              placeholder="Enter your email address"
            />

            <Input
              label="Company or Organisation"
              type="text"
              name="organization"
              value={formData.organization}
              onChange={handleChange}
              required
              autoComplete="organization"
              placeholder="Enter your company or organisation"
            />

            <Button
              type="submit"
              variant="primary"
              block
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Saving...
                </>
              ) : (
                'Save and Continue'
              )}
            </Button>
          </form>

          {message.text && (
            <Message type={message.type}>{message.text}</Message>
          )}
        </Card>
      </div>
    </div>
  );
};

export default CompleteProfile;
